import type { TKey } from './types';

const en: Record<TKey, string> = {
  'nav.library':            'Library',
  'nav.myBooks':            'My Books',
  'nav.credentials':        'Credentials',
  'nav.mindmap':            'Mindmap',
  'nav.reading':            'Reading',
  'nav.upload':             'Upload',
  'nav.signin':             'Sign in',
  'nav.signout':            'Sign out',
  'nav.localeSelect':       'Language',

  'brand.sub':              'BookQuest',
  'onboard.welcome':        'Start with one book.',
  'onboard.welcomeSub':     'Pick a curated title or upload your own PDF — receive a four-layer mindmap, nine-tier adaptive quiz, and lifelong credentials.',
  'onboard.featureMapTitle':  'Four-layer mindmap',
  'onboard.featureMapBody':   'From surface narrative (L1) to the unsaid premises (L4) — a knowledge map drawn across four layers by AI.',
  'onboard.featureQuizTitle': 'Nine-tier adaptive quiz',
  'onboard.featureQuizBody':  'IRT-based difficulty (T1–T9) adjusts to your understanding in real time.',
  'onboard.featureCredTitle': 'Lifelong credentials',
  'onboard.featureCredBody':  'Every completion is issued as a W3C Verifiable Credential — externally verifiable, kept for life.',
  'onboard.ctaBrowse':      'Browse curated books',
  'onboard.ctaUpload':      'Upload my PDF',
  'onboard.signedOutHint':  'Sign in first to save your progress.',
  'onboard.ctaSignIn':      'Sign in / Sign up',
  'common.loading':         'Loading…',
  'common.offline':         'offline',
  'common.free':            'Free',
  'common.error':           'error',
  'common.back':            '← Library',
  'common.continue':        'Continue',
  'common.submit':          'Submit',
  'common.skip':            'Skip',
  'common.next':            'Next',
  'common.finish':          'Finish',
  'common.startAssessment': 'Start assessment',
  'theme.label':            'Theme',
  'theme.light':            'Light',
  'theme.dark':             'Dark',
  'theme.system':           'System',
  'sound.label':            'Sound',
  'sound.on':               'On',
  'sound.off':              'Off',
  'api.error.badRequest':   'The request was rejected.',
  'api.error.unauthorized': 'Please sign in to continue.',
  'api.error.forbidden':    'You don’t have access to this resource.',
  'api.error.notFound':     'Not found — the resource may have moved or been removed.',
  'api.error.tooLarge':     'File is too large. Try again under 100 MB.',
  'api.error.unsupported':  'Unsupported file format.',
  'api.error.rateLimited':  'Too many requests — try again in a moment.',
  'api.error.server':       'Server error — please try again shortly.',
  'api.error.network':      'Network connection lost.',
  'api.error.unknown':      'Unknown error',
  'a11y.skipToMain':        'Skip to main content',
  'notFound.kicker':        'Not Found · 404',
  'notFound.titleLine1':    'This path',
  'notFound.titleLine2':    'is not on the shelf.',
  'notFound.lead':          'The book moved, or this page never existed. Return to the library and begin again.',
  'notFound.pathLabel':     'Requested path',
  'notFound.cta':           'Back to library',
  'footer.tagline':         'A library where every book becomes a mindmap and a credential',
  'footer.navLabel':        'Footer navigation',
  'footer.about':           'About',
  'footer.privacy':         'Privacy',
  'footer.terms':           'Terms',
  'footer.contact':         'Contact',
  'reader.controls':              'Reader controls',
  'reader.size':                  'Size',
  'reader.theme':                 'Theme',
  'reader.previewFallback':       'Body preview not yet ready. It appears once the book is analyzed.',
  'reader.stubNotice':            'Reader environment preview. Full body rendering ships next release.',
  'reader.demoBody1':             'Key concepts, counter-arguments, definitions, and action items appear marked by color while you read.',
  'reader.demoBody2':             'Terms are defined inline,',
  'reader.demoBody3':             'and what the reader should try is',
  'reader.demoHighlightYellow':   'a key concept',
  'reader.demoHighlightCoral':    'a counter-argument',
  'reader.demoHighlightBlue':     'a definition',
  'reader.demoHighlightMint':     'an action item',
  'reader.aside.legendLabel':     'Highlight legend',
  'reader.aside.legendYellow':    'Key concept',
  'reader.aside.legendCoral':     'Counter-argument',
  'reader.aside.legendBlue':      'Definition',
  'reader.aside.legendMint':      'Action item',
  'reader.aside.metaLabel':       'Book',
  'common.correct':         'Correct.',
  'common.notQuite':        'Not quite.',
  'common.save':            'Save for later',
  'common.beginReading':    'Begin reading',
  'common.openMindmap':     'Open mindmap',
  'common.searchPlaceholder': 'Search by title, author, ISBN…',

  'library.kicker':         'Opening Collection · 100 vols',
  'library.heroLine1':      '100 to begin,',
  'library.heroLine2':      'an endless shelf to come.',
  'library.heroSub':        'The opening collection — every book reborn as a four-layer mindmap and a nine-tier assessment.',
  'library.featured':       'Featured this month',
  'library.curatedCount':   'curated',
  'library.noBooks':        'No curated books yet. Use seed script or admin curation.',
  'library.filterAll':      'All',
  'library.filterClassics': 'Classics',
  'library.filterPhilosophy': 'Philosophy',
  'library.filterK5':       'K-5',
  'library.filter68':       '6-8',
  'library.filter912':      '9-12',

  'detail.previewNodes':    'The four-layer mindmap.',
  'detail.sampledQuestions': 'Three questions, sampled.',
  'detail.bookMissing':     'Book metadata missing.',
  'detail.noQuestions':     'No questions yet — pipeline may still be processing.',
  'detail.byAuthor':        'by',
  'detail.pages':           'pages',
  'detail.metaSeparator':   ' · ',
  'detail.sourceLabel':            'Source',
  'detail.firstQuoteLabel':        'First quote',
  'detail.relatedCredentialLabel': 'Related credential',
  'detail.relatedCredentialBody':  'This book unlocks a Major-tier credential upon completion.',

  'map.fourLayers':         'Four layers',
  'map.navigation':         'Navigation',
  'map.surface':            'L1 Surface',
  'map.argument':           'L2 Argument',
  'map.concept':            'L3 Concept',
  'map.meta':               'L4 Meta — the unsaid',
  'map.notReady':           'Map not ready — pipeline may still be processing.',
  'map.loadingMap':         'Loading map…',
  'map.selectNode':         'Select a node to inspect',
  'map.selectNodeSub':      'Each node carries a citation (L1), an argument (L2), a concept (L3) or a silent premise (L4).',
  'map.hoverHint':          'Hover a node',

  'quiz.of':                ' of ',
  'quiz.minRemaining':      'min remaining',
  'quiz.noQuestions':       'No questions available.',
  'quiz.tier':              'Tier',
  'quiz.celebration.kicker':         'Completed',
  'quiz.celebration.titleLine1':     'A book',
  'quiz.celebration.titleLine2':     'completed.',
  'quiz.celebration.lead':           'Your assessment is sealed into a credential — kept for life.',
  'quiz.celebration.correct':        'Correct',
  'quiz.celebration.theta':          'Final θ',
  'quiz.celebration.viewCredential': 'View credential',

  'creds.kicker':           'Issued',
  'creds.heroLine':         'The shelf of your',
  'creds.heroEm':           'earned',
  'creds.heroSub':          'Every milestone issued as W3C Verifiable Credentials — externally verifiable, kept for life.',
  'creds.master':           'Master ★',
  'creds.recent':           'Recently issued.',
  'creds.pastDays':         'past 30 days',
  'creds.noneYet':          'Complete a book to earn your first credential.',
  'creds.urn':              'URN',
  'creds.issued':           'Issued',
  'creds.anchor':           'Anchor',
  'creds.tier.micro':       'Micro',
  'creds.tier.standard':    'Standard',
  'creds.tier.major':       'Major',
  'creds.tier.master':      'Master',
  'creds.tier.legendary':   'Legendary',
  'creds.count':            'credentials',
  'creds.aside.tiersLabel':    '5-Tier ladder',
  'creds.aside.tiersBody':     'Micro → Standard → Major → Master → Legendary. 80–100 earned over a lifetime.',
  'creds.aside.verifyLabel':   'External verification',
  'creds.aside.verifyBody':    'Each URN follows W3C Verifiable Credentials — verifiable by any compliant verifier.',
  'creds.aside.standardLabel': 'Standard',
  'creds.justIssued':          'Just issued',
  'creds.copied':              'Copied',
  'creds.copyFailed':          'Copy failed',
  'creds.share':               'Share',
  'creds.verifyExternally':    'Verify externally',
  'creds.verifierComing':      'Verifier portal coming soon.',
  'creds.shareNative':         'Share',
  'creds.print':               'Print · PDF',
  'source.curated':         'curated',
  'source.user_upload':     'user upload',
  'source.partner':         'partner',
  'source.public_domain':   'public domain',
  'source.institution':     'institution',

  'auth.signinTitle':       'Sign in',
  'auth.signupTitle':       'Sign up',
  'auth.magicTitle':        'Magic link',
  'auth.email':             'Email',
  'auth.password':          'Password',
  'auth.signin':            'Sign in',
  'auth.signup':            'Create account',
  'auth.sendMagic':         'Send magic link',
  'auth.switchToSignup':    'New here? Create account',
  'auth.switchToSignin':    'Already have an account? Sign in',
  'auth.switchToMagic':     'Use magic link instead',
  'auth.notConfiguredBanner': 'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in packages/web-app/.env.local.',
  'auth.checkEmail':        'Check your email to confirm your account.',
  'auth.magicSent':         'Magic link sent — check your email.',
  'auth.working':           'Working…',
  'auth.aside.whyLabel':       'Why BookQuest',
  'auth.aside.why1':           'A book becomes a 4-Layer mindmap.',
  'auth.aside.why2':           'A 9-Tier assessment measures reading.',
  'auth.aside.why3':           'Lifelong credentials accumulate.',
  'auth.aside.privacyLabel':   'Privacy',
  'auth.aside.privacyBody':    'Your reading data stays yours.',

  'upload.title':           'Upload a book',
  'upload.help':            'PDFs only. The system de-duplicates globally — if anyone has already processed this file you get instant free access.',
  'upload.fileLabel':       'PDF file',
  'upload.countryLabel':    'Country (for pricing)',
  'upload.submit':          'Upload',
  'upload.submitting':      'Uploading…',
  'upload.chooseFirst':     'Choose a PDF first.',
  'upload.failed':          'Upload failed',
  'upload.resultTitle':     'Result',
  'upload.dedupeStatus':    'De-dup status',
  'upload.paymentStatus':   'Payment status',
  'upload.amount':          'Amount',
  'upload.jobId':           'Job ID',
  'upload.eta':             'ETA',
  'upload.watchProgress':   'Watch progress',
  'job.title':              'Analysis progress',
  'job.processing':         'Analyzing the book and generating questions…',
  'job.stage':              'Stage',
  'job.cost':               'Cost so far',
  'job.done':               'Completed',
  'job.failed':             'Something went wrong during processing',
  'job.openBook':           'Go to book',
  'job.eta':                'Usually takes ~20 min. Keeps running in the background if you close this.',
  'upload.aside.formatLabel':  'Supported formats',
  'upload.aside.formatBody':   'PDF with extractable text. Up to 50 MB.',
  'upload.aside.etaLabel':     'Estimated time',
  'upload.aside.etaBody':      '5–15 minutes, depending on length and queue.',
  'upload.aside.privacyLabel': 'Privacy',
  'upload.aside.privacyBody':  'Uploaded PDFs are discarded after analysis.',
  'nav.achievements':       'Achievements',
  'nav.admin':              'Admin',
  'gam.kicker':             'Your journey',
  'gam.heroLine':           'Read, solve,',
  'gam.heroEm':             'level up',
  'gam.heroSub':            'Every question you answer and book you finish earns XP and badges. Climb the levels and leave your mark on the leaderboard.',
  'gam.levelLabel':         'Level',
  'gam.xpTotal':            'Total XP',
  'gam.xpUnit':             'XP',
  'gam.toNext':             'to next level',
  'gam.maxLevel':           'Max level',
  'gam.badges':             'Badges',
  'gam.badgesSub':          'Marks of your learning',
  'gam.locked':             'Locked',
  'gam.noBadges':           'No badges yet — start with your first question.',
  'gam.leaderboard':        'Leaderboard',
  'gam.leaderboardSub':     'XP ranking',
  'gam.rank':               'Rank',
  'gam.you':                'You',
  'gam.emptyBoard':         'No rankings yet.',
  'gam.period.daily':       'Today',
  'gam.period.weekly':      'This week',
  'gam.period.monthly':     'This month',
  'gam.period.all':         'All time',
  'gam.howXp':              'How XP works',
  'gam.howXpSub':           'Here is how XP adds up',
  'gam.levelCurve':         'Level curve',
  'gam.levelN':             'Lv',
  'gam.aside.label':        'What is XP?',
  'gam.aside.body':         'XP is the running record of your learning. Correct answers, completed stages, and finished books all add up into levels.',
  'gam.aside.fairLabel':    'Fair earning',
  'gam.aside.fairBody':     'Re-answering the same question only earns XP once, on your first correct answer.',
  'gam.xpRule.answer_correct':     'Correct answer',
  'gam.xpRule.stage_complete':     'Stage complete',
  'gam.xpRule.book_clear':         'Book cleared',
  'gam.xpRule.cr_submit':          'Written response',
  'gam.xpRule.streak_day':         'Daily streak',
  'gam.xpRule.question_propose':   'Propose a question',
  'gam.xpRule.question_adopted':   'Question adopted',
  'gam.xpRule.review_participate': 'Review participation',
  'detail.propose':            'Propose a question',
  'prop.title':                'Propose a question',
  'prop.help':                 'Write a multiple-choice question for this book. If an admin adopts it, it joins the question bank and you earn XP and a badge.',
  'prop.stemLabel':            'Question (prompt)',
  'prop.stemPlaceholder':      'e.g. Why does the protagonist leave in this chapter?',
  'prop.choicesLabel':         'Choices (select the correct one)',
  'prop.choiceCorrect':        'Correct',
  'prop.choicePlaceholder':    'Choice text',
  'prop.explanationLabel':     'Explanation (optional)',
  'prop.explanationPlaceholder': 'Justify the answer with evidence from the text.',
  'prop.submit':               'Submit proposal',
  'prop.submitting':           'Submitting…',
  'prop.success':              'Proposal received. We will let you know if it is adopted.',
  'prop.needStem':             'Enter the question text.',
  'prop.needChoices':          'Fill in at least two choices.',
  'prop.needCorrect':          'Select the correct choice.',
  'prop.myProposals':          'My proposals',
  'prop.none':                 'No proposals yet.',
  'prop.status.pending':       'Under review',
  'prop.status.accepted':      'Adopted',
  'prop.status.rejected':      'Declined',
  'prop.aside.howLabel':       'How it works',
  'prop.aside.howBody':        'Propose → admin review → on adoption it joins the bank (served after further QA). Better questions get adopted more often.',
  'prop.aside.rewardLabel':    'Rewards',
  'prop.aside.rewardBody':     'Proposing earns XP; adoption earns more XP plus the “Question proposer” badge.',
  'prop.difficulty':           'Suggested difficulty (optional)',
  'prop.difficultyNone':       'Unspecified',
  'detail.report':            'Achievement report',
  'quiz.celebration.viewReport': 'View report',
  'report.kicker':            'Assessment report',
  'report.title':             'Achievement report',
  'report.accuracy':          'Overall accuracy',
  'report.level':             'Proficiency',
  'report.theta':             'Ability estimate (θ)',
  'report.thetaNote':         'Relative estimate — approximate, uncalibrated',
  'report.attempted':         'Attempted',
  'report.unit':              'items',
  'report.naep':              'NAEP cognitive domains',
  'report.lidia':             'LIDIA domains',
  'report.trend':             'Growth trend',
  'report.window':            'Window',
  'report.notAttempted':      'Not attempted',
  'report.empty':             'No assessment records yet',
  'report.emptySub':          'Solve questions and your domain mastery will appear here.',
  'report.startQuiz':         'Start assessment',
  'report.honestyLabel':      'About these metrics',
  'report.honestyBody':       'Levels and θ are relative, uncalibrated approximations — not standardized scores (Lexile, exam grades).',
  'report.viewCredential':    'View credential',
  'report.viewAchievements':  'Achievements · XP',
  'report.retry':             'Retry',
  'report.naep.locate_recall':       'Locate & Recall',
  'report.naep.integrate_interpret': 'Integrate & Interpret',
  'report.naep.critique_evaluate':   'Critique & Evaluate',
  'footer.larc':              'Review board',
  'larc.kicker':             'Trust & transparency',
  'larc.title':              'LARC Review Board',
  'larc.lead':               'Every item is AI-generated, then passes multi-stage review. LARC makes that process public.',
  'larc.missionLabel':       'Mission',
  'larc.missionBody':        'We safeguard the fairness and accuracy of assessment. AI-generated items are verified by humans and cross-models for trustworthy literacy measurement.',
  'larc.criteriaLabel':      'Review criteria',
  'larc.crit1':              'Evidence — is the answer actually grounded in the text?',
  'larc.crit2':              'Clarity — are the stem and choices unambiguous?',
  'larc.crit3':              'Discrimination — are distractors plausible and the key unique?',
  'larc.crit4':              'Grade & bias — appropriate for the grade band, free of bias?',
  'larc.statsLabel':         'Review activity',
  'larc.stApproved':         'Approved',
  'larc.stPending':          'In LARC queue',
  'larc.stReviewed':         'AI reviewed',
  'larc.stRejected':         'Rejected',
  'larc.stTotal':            'Total items',
  'larc.stReports':          'Reports resolved',
  'larc.note':               'Only aggregate counts are shown — no personal data or item content.',
  'prelearn.title':          'Pre-learning',
  'prelearn.subtitle':       'Background before you read',
  'prelearn.books':          'Buy · bookstores',
  'prelearn.media':          'Video · background',
  'prelearn.note':           'Opens an external site search.',
  'creds.summaryLabel':      'Overall literacy credential',
  'creds.summaryTotal':      'Total credentials',
  'creds.summaryBooks':      'Books covered',
  'creds.summaryHighest':    'Highest tier',
  'nav.vault':               'Vault',
  'vault.title':             'Knowledge Vault',
  'vault.subtitle':          'Collect memos, quotes and insights',
  'vault.kindMemo':          'Memo',
  'vault.kindQuote':         'Quote',
  'vault.kindInsight':       'Insight',
  'vault.kindFeedback':      'Feedback',
  'vault.contentPlaceholder': 'Write what you want to remember',
  'vault.tagsPlaceholder':   'Tags (comma-separated)',
  'vault.add':               'Save',
  'vault.searchPlaceholder': 'Search content',
  'vault.allKinds':          'All',
  'vault.empty':             'No notes saved yet.',
  'vault.delete':            'Delete',
  'vault.added':             'Saved.',
  'vault.deleted':           'Deleted.',
  'vault.needContent':       'Enter some content.',
  'vault.export':            'Export',
  'vault.exportMd':          'Markdown',
  'vault.exportJson':        'JSON',
  'verify.title':            'Credential verification',
  'verify.checking':         'Verifying…',
  'verify.valid':            'Valid credential',
  'verify.invalid':          'Invalid or revoked credential',
  'verify.notFound':         'Credential not found',
  'verify.tier':             'Tier',
  'verify.issued':           'Issued',
  'verify.note':             'Verified against BookQuest issuance records. No personal data is exposed.',
};

export default en;
